import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Col } from "antd";
import { ErrorMessage } from "formik";
import styled from "styled-components";
import { INPUT_VARIANT } from "@/common/elements/InputElements/const";
import TextField from "@/common/elements/InputElements/TextField";
import FormItem from "@/common/elements/InputElements/FormItem.jsx/index.jsx";
import Select from "@/common/elements/InputElements/Select";
import CommonDatePicker from "@/common/elements/DatePicker";
import TimePicker from "@/common/elements/TimePicker";
import { StyledDate } from "@/components/InmateDetails/styles";
import {
  getSQLTypeToHTMLType,
  getOptionsFromRefList,
  findAgefromDOB,
  generateRules,
  isNonEmptyVariable,
  hasCheckSecurity,
  normalizeUnknownSsn,
} from "@/util/common_function";
import { StyledFlex } from "../styles";
import Checkbox from "../elements/InputElements/CheckBox";
import { useSelector } from "react-redux";
import { PERSON } from "@/components/InmateDetails/Personal";
import { useFieldSecurity } from "@/contexts/FieldSecurityContext";
import {
  useFieldVerification,
  makeVerifyKeyDownCapture,
} from "@/contexts/FieldVerificationContext";
import { useVocab, keepBlankLabelSpace } from "@/hooks/useVocab";

const StyledColumn = styled(Col)`
  .ant-col {
    min-height: auto !important;
  }
`;

const DynamicForm = ({
  formAttributes,
  i,
  form,
  person,
  bookingsFieldsEnabled = true,
  view = true,
  edit = true,
  fullSearch = false,
  highlight = false,
  startingPoint = "",
}) => {
  const { isScheduledBooking, bookings } = useSelector(
    (state) => state?.bookings,
  );
  const {
    label,
    columnName,
    originalColumnName,
    tableName,
    type,
    refListName,
    mixedCase,
    isID,
    isStaff,
    disabled,
    canSelectFutureDate = false,
    disablePastDate = false,
    showSearch = true,
    longStringRequired = formAttributes.longStringRequired ||
      ["teeth", "hairStyle"].includes(columnName),
    maxLength = -1,
  } = formAttributes;

  const htmlType = getSQLTypeToHTMLType(type);

  const newColumnName = Array.isArray(columnName)
    ? columnName[columnName?.length - 1]
    : columnName;
  // Global Client Params rows (e.g. RegularExpression / "BioExtra:Alien_Registration_#_U")
  // are authored against the raw API/DB column name, not the camelCased
  // `columnName` convertApiFields derives for form binding (e.g.
  // "alienRegistration") - so dynamic *Extra table fields must look up
  // validation by originalColumnName when it's present. Static/hardcoded
  // field definitions (no originalColumnName) fall back to columnName as
  // before, since those are already authored to match ClientParams directly.
  let paramContext = tableName + ":" + (originalColumnName || newColumnName);

  // Site label rename (legacy Vocab()), matched on the label or the raw DB
  // column name. Used for the label, placeholder and validation messages so
  // they agree; logic below that tests the label keeps using `label`.
  const vocab = useVocab();
  const shownLabel = vocab(label, originalColumnName || newColumnName);
  const formLabel = keepBlankLabelSpace(label, shownLabel);

  const { requireVerification, isUnverified, markVerified } =
    useFieldVerification();
  const unverified = requireVerification && isUnverified(newColumnName);
  const verifyKeyDownCapture = makeVerifyKeyDownCapture(
    newColumnName,
    unverified,
    markVerified,
  );
  const verifyOnChange = unverified
    ? () => markVerified(newColumnName)
    : undefined;

  const highlightClass = highlight || unverified ? "field--pink" : "";
  const highlightClassSelect =
    highlight || unverified ? "address-select--pink" : "";

  // When rendered inside a FieldSecurityProvider (entity is non-empty),
  // derive view/edit from context + hasCheckSecurity instead of relying on props.
  const { entity: ctxEntity, startingPoint: ctxStartingPoint } =
    useFieldSecurity();
  const resolvedView = ctxEntity
    ? hasCheckSecurity(
        "Functionality",
        "field",
        "VIEW",
        "",
        "",
        ctxEntity,
        newColumnName,
        ctxStartingPoint,
      )
    : view;
  const resolvedEdit = ctxEntity
    ? hasCheckSecurity(
        "Functionality",
        "field",
        "MODIFY",
        "",
        "",
        ctxEntity,
        newColumnName,
        ctxStartingPoint,
      )
    : edit;

  const isAgencyInmate = columnName == "agency" && person == "INMATE";

  // In the Warrants module, Last Name / First Name are always mandatory.
  // Once at least one warrant record exists on the root, Race, Gender,
  // Height, Weight, Hair Color, Eye Color and DOB become mandatory too.
  const isWarrants = startingPoint === "Warrants";
  const hasWarrantRecords = (bookings?.warrants ?? []).length > 0;
  const warrantExtendedRequiredFields = [
    "race",
    "sex",
    "height",
    "weight",
    "hair",
    "eyes",
    "dob",
  ];
  const isWarrantExtendedRequired =
    isWarrants &&
    hasWarrantRecords &&
    warrantExtendedRequiredFields.includes(columnName);
  const setRequired = isWarrants
    ? ["lName", "fName"].includes(columnName) || isWarrantExtendedRequired
    : !isAgencyInmate;

  const buildRules = (ctx, lbl, required) => {
    const rules = generateRules(ctx, lbl, required);
    if (isWarrantExtendedRequired && !rules.some((r) => r.required)) {
      rules.push({ required: true, message: `${lbl} is required` });
    }
    return rules;
  };

  // SSN: legacy rewrites anything starting with "UNK" to "UNKNOWN" on
  // validation (skipped while searching) - mirror that when the field loses
  // focus so the stored value matches PCI.
  const ssnOnBlur =
    String(newColumnName).toLowerCase() === "ssn" && !fullSearch
      ? (e) => {
          const normalized = normalizeUnknownSsn(e?.target?.value);
          if (normalized !== e?.target?.value) {
            form?.setFieldValue(columnName, normalized);
            form?.validateFields([columnName]).catch(() => {});
          }
        }
      : undefined;

  const [isScheduledBookingLocal, setisScheduledBookingLocal] = useState(false);

  useEffect(() => {
    if (["Last Name", "First Name"]?.findIndex((d) => d === label) !== -1) {
      setisScheduledBookingLocal(() => false);
    } else {
      setisScheduledBookingLocal(() => isScheduledBooking);
    }
  }, [label, isScheduledBooking]);

  if (person === PERSON.STAFF || person === PERSON.VISITOR_LOG) {
    if (["Race", "Gender", "DOB"]?.findIndex((d) => d === label) !== -1) {
      paramContext = "";
    }
  }

  return (
    <React.Fragment>
      {
        // checkField(paramContext) &&
        <StyledColumn key={i} span={24 / 4}>
          {(htmlType === "text" || htmlType === "number") && (
            <>
              <FormItem
                name={columnName}
                label={formLabel}
                skipVocab
                rules={
                  !fullSearch
                    ? !isScheduledBookingLocal
                      ? buildRules(paramContext, shownLabel, setRequired)
                      : []
                    : []
                }
              >
                <TextField
                  type={htmlType}
                  placeholder={shownLabel}
                  disabled={
                    resolvedEdit
                      ? !fullSearch
                        ? disabled || !bookingsFieldsEnabled
                        : false
                      : true
                  }
                  className={`${resolvedView ? "" : "input-control-mask visibility-hidden"} ${highlightClass}`.trim()}
                  maxLength={maxLength !== -1 ? maxLength : ""}
                  mixedCase={mixedCase}
                  onChange={verifyOnChange}
                  onKeyDown={verifyKeyDownCapture}
                  onBlur={ssnOnBlur}
                />
              </FormItem>
            </>
          )}
          {htmlType === "textarea" && (
            <>
              <FormItem
                name={columnName}
                label={formLabel}
                skipVocab
                rules={
                  !fullSearch
                    ? !isScheduledBookingLocal
                      ? buildRules(paramContext, shownLabel, setRequired)
                      : []
                    : []
                }
              >
                <TextField
                  inputType={htmlType}
                  type={htmlType}
                  placeholder={shownLabel}
                  disabled={
                    resolvedEdit ? disabled || !bookingsFieldsEnabled : true
                  }
                  className={
                    resolvedView ? "" : "input-control-mask visibility-hidden"
                  }
                  mixedCase={mixedCase}
                />
              </FormItem>
            </>
          )}
          {htmlType === "select" && (
            <>
              <FormItem
                name={columnName}
                label={formLabel}
                skipVocab
                rules={
                  !fullSearch
                    ? !isScheduledBookingLocal
                      ? buildRules(paramContext, shownLabel, setRequired)
                      : []
                    : []
                }
              >
                <Select
                  options={getOptionsFromRefList(
                    refListName || columnName,
                    isStaff ? true : false,
                    longStringRequired ? true : false,
                    isID ? true : false,
                  )}
                  placeholder={shownLabel}
                  showSearch={showSearch}
                  variant="outlined"
                  disabled={
                    resolvedEdit ? (bookingsFieldsEnabled ? false : true) : true
                  }
                  className={`${resolvedView ? "" : "input-control-mask visibility-hidden"} ${highlightClassSelect}`.trim()}
                  onChange={verifyOnChange}
                  onKeyDown={verifyKeyDownCapture}
                ></Select>
              </FormItem>
            </>
          )}
          {htmlType === "datetime" && (
            <FormItem
              name={columnName}
              label={formLabel}
              skipVocab
              rules={
                !fullSearch
                  ? !isScheduledBookingLocal
                    ? buildRules(paramContext, shownLabel, setRequired)
                    : []
                  : []
              }
              initialValue={columnName === "arrestDateTime" ? "" : ""}
            >
              <CommonDatePicker
                placeholder="MM/DD/YYYY HH:mm"
                format="MM/DD/YYYY HH:mm"
                showTime={true}
                disableFutureDates={canSelectFutureDate}
                inputProps={{
                  maxLength: 16,
                }}
                disabled={
                  resolvedEdit ? (bookingsFieldsEnabled ? false : true) : true
                }
                className={`${resolvedView ? "" : "input-control-mask visibility-hidden"} ${highlightClass}`.trim()}
                onChange={() => verifyOnChange?.()}
                onKeyDown={verifyKeyDownCapture}
              />
            </FormItem>
          )}
          {htmlType === "date" && (
            <FormItem
              name={columnName}
              label={formLabel}
              skipVocab
              rules={
                !fullSearch
                  ? !isScheduledBookingLocal
                    ? buildRules(paramContext, shownLabel, setRequired)
                    : []
                  : []
              }
              initialValue={""}
            >
              <CommonDatePicker
                placeholder="MM/DD/YYYY"
                format="MM/DD/YYYY"
                showTime={false}
                isDOB={columnName === "dob"}
                disableFutureDates={columnName == "dob"}
                disablePastDate={disablePastDate}
                inputProps={{
                  maxLength: 10,
                }}
                disabled={
                  resolvedEdit ? (bookingsFieldsEnabled ? false : true) : true
                }
                className={`${resolvedView ? "" : "input-control-mask visibility-hidden"} ${highlightClass}`.trim()}
                onChange={() => verifyOnChange?.()}
                onKeyDown={verifyKeyDownCapture}
              />
            </FormItem>
          )}
          {htmlType === "time" && (
            <FormItem
              name={columnName}
              label={formLabel}
              skipVocab
              rules={
                !fullSearch
                  ? !isScheduledBookingLocal
                    ? buildRules(paramContext, shownLabel, setRequired)
                    : []
                  : []
              }
              initialValue={""}
            >
              <TimePicker
                placeholder="HH:mm"
                format="HH:mm"
                disabled={
                  resolvedEdit ? (bookingsFieldsEnabled ? false : true) : true
                }
                className={`${resolvedView ? "" : "input-control-mask visibility-hidden"} ${highlightClass}`.trim()}
                onChange={() => verifyOnChange?.()}
                onKeyDown={verifyKeyDownCapture}
              />
            </FormItem>
          )}
          {htmlType === "checkbox" && (
            <StyledFlex align="center" style={{ height: "100%" }}>
              <FormItem
                name={columnName}
                valuePropName="checked"
                label={formLabel}
                skipVocab
                layout="horizontal"
                className="form-item-checkbox-left"
                style={{ marginBottom: "0" }}
              >
                <Checkbox
                  className={
                    resolvedView ? "" : "input-control-mask visibility-hidden"
                  }
                  disabled={
                    resolvedEdit ? (bookingsFieldsEnabled ? false : true) : true
                  }
                />
              </FormItem>
            </StyledFlex>
          )}
        </StyledColumn>
      }
    </React.Fragment>
  );
};

export default DynamicForm;
